/**
 * Slice 9.1 — Policies admin tab.
 *
 * Read-only listing of compliance policies, with drill into rule list.
 * Tenant-policy authoring (create / edit / disable) is deferred to a
 * follow-up slice — needs more design work for the rule-builder UI.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';

interface PolicyListItem {
  id: string;
  kb_origin: 'System' | 'Tenant';
  short_code: string;
  name: string;
  version: string;
  description: string | null;
  associated_standards: string | null;
  source_citation: string;
  is_active: number | boolean;
  status: 'Active' | 'Deprecated';
  rule_count: number;
}

interface PolicyDetailRule {
  id: string;
  algorithm_canonical_id: string | null;
  algorithm_canonical_name: string | null;
  algorithm_family: string | null;
  usage_scope: string;
  min_key_size: number | null;
  max_key_size: number | null;
  allowed_curves_json: string[] | null;
  verdict: 'Allowed' | 'Not_allowed' | 'Conditional';
  condition_text: string | null;
  rule_order: number;
  source_citation: string | null;
}

interface PolicyDetail extends Omit<PolicyListItem, 'rule_count'> {
  rules: PolicyDetailRule[];
}

export function PoliciesAdminTab() {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const policiesQuery = useQuery<PolicyListItem[]>({
    queryKey: ['admin-policies'],
    queryFn: () => api.get<PolicyListItem[]>('/admin/policies'),
  });

  const detailQuery = useQuery<PolicyDetail>({
    queryKey: ['admin-policies', selectedId],
    enabled: !!selectedId,
    queryFn: () => api.get<PolicyDetail>(`/admin/policies/${selectedId!}`),
  });

  if (policiesQuery.isLoading) {
    return <div className="p-6 text-ink-500 italic text-sm">Loading policies…</div>;
  }
  if (policiesQuery.isError) {
    return <div className="p-6 text-red-700 text-sm">Failed to load policies.</div>;
  }
  const policies = policiesQuery.data ?? [];

  return (
    <div className="grid grid-cols-3 gap-4">
      {/* Left column: policy list */}
      <div className="col-span-1">
        <h3 className="text-sm font-display text-ink mb-2">
          {policies.length} polic{policies.length === 1 ? 'y' : 'ies'}
        </h3>
        <ul className="space-y-1">
          {policies.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => setSelectedId(p.id)}
                className={`w-full text-left px-3 py-2 border rounded text-sm ${
                  selectedId === p.id
                    ? 'border-accent bg-accent-light'
                    : 'border-ink-200 hover:bg-ink-50'
                }`}
              >
                <div className="flex justify-between items-baseline gap-2">
                  <span className="font-medium">{p.name}</span>
                  <span className={`text-xs ${p.kb_origin === 'System' ? 'text-blue-700' : 'text-purple-700'}`}>
                    {p.kb_origin}
                  </span>
                </div>
                <div className="text-xs text-ink-500 mt-0.5 font-mono">{p.short_code}</div>
                <div className="text-xs text-ink-400 mt-0.5">
                  v{p.version} · {p.rule_count} rule{p.rule_count !== 1 ? 's' : ''}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Right column: detail */}
      <div className="col-span-2">
        {!selectedId ? (
          <div className="border border-dashed border-ink-200 rounded p-6 text-center text-ink-400 text-sm">
            Select a policy to view its rules.
          </div>
        ) : detailQuery.isLoading ? (
          <div className="p-6 text-ink-500 italic text-sm">Loading rules…</div>
        ) : detailQuery.isError || !detailQuery.data ? (
          <div className="p-6 text-red-700 text-sm">Failed to load policy detail.</div>
        ) : (
          <PolicyDetailView policy={detailQuery.data} />
        )}
      </div>
    </div>
  );
}

function PolicyDetailView({ policy }: { policy: PolicyDetail }) {
  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-display text-ink mb-1">{policy.name}</h2>
        <div className="text-xs text-ink-500 font-mono mb-2">
          {policy.short_code} · v{policy.version} · {policy.kb_origin}
        </div>
        {policy.description && (
          <p className="text-sm text-ink-600 mb-2">{policy.description}</p>
        )}
        <p className="text-xs text-ink-500">
          <strong>Source:</strong> {policy.source_citation}
        </p>
      </div>

      <h3 className="text-sm font-display text-ink mb-2">
        Rules ({policy.rules.length})
      </h3>
      <div className="border border-ink-200 rounded overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-ink-50 text-ink-500 uppercase tracking-wide">
            <tr>
              <th className="px-2 py-2 text-left">Order</th>
              <th className="px-2 py-2 text-left">Algorithm</th>
              <th className="px-2 py-2 text-left">Scope</th>
              <th className="px-2 py-2 text-left">Size constraint</th>
              <th className="px-2 py-2 text-left">Verdict</th>
              <th className="px-2 py-2 text-left">Notes / source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {policy.rules.map((r) => (
              <tr key={r.id}>
                <td className="px-2 py-1 font-mono">{r.rule_order}</td>
                <td className="px-2 py-1 font-mono">
                  {r.algorithm_canonical_name ?? <span className="text-ink-300">any</span>}
                </td>
                <td className="px-2 py-1">{r.usage_scope}</td>
                <td className="px-2 py-1">
                  {r.min_key_size !== null || r.max_key_size !== null ? (
                    <span className="font-mono">
                      {r.min_key_size ?? 0}–{r.max_key_size ?? '∞'}
                    </span>
                  ) : <span className="text-ink-300">—</span>}
                </td>
                <td className="px-2 py-1">
                  <VerdictBadge verdict={r.verdict} />
                </td>
                <td className="px-2 py-1 text-ink-500">
                  {r.condition_text && <div className="italic">{r.condition_text}</div>}
                  {r.source_citation && (
                    <div className="text-ink-400 mt-0.5">{r.source_citation}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VerdictBadge({ verdict }: { verdict: 'Allowed' | 'Not_allowed' | 'Conditional' }) {
  const styles = {
    Allowed: 'bg-green-50 text-green-700 border-green-200',
    Not_allowed: 'bg-red-50 text-red-700 border-red-200',
    Conditional: 'bg-amber-50 text-amber-700 border-amber-200',
  } as const;
  return (
    <span className={`px-2 py-0.5 text-xs rounded border ${styles[verdict]}`}>
      {verdict.replace('_', ' ')}
    </span>
  );
}
